import React from "react";
import HeroManual from "./HeroManual";
import useHeroBase from "./hooks/useHeroBase";
import useHeroSlots from "./hooks/useHeroSlots";
import HeroCinematic from "./hero-modes/HeroCinematic";
import HeroSplit from "./hero-modes/HeroSplit";
import HeroCard from "./hero-modes/HeroCard";
import HeroCentered from "./hero-modes/HeroCentered";
import HeroGallery from "./hero-modes/HeroGallery";
import HeroProfile from "./hero-modes/HeroProfile";
import HeroMagazine from "./hero-modes/HeroMagazine";
import HeroStack from "./hero-modes/HeroStack";
import HeroMosaic from "./hero-modes/HeroMosaic";
import HeroSpotlightSwap from "./hero-modes/HeroSpotlightSwap";
import HeroPhotoBanner from "./hero-modes/HeroPhotoBanner";
import HeroMagazineSwap from "./hero-modes/HeroMagazineSwap";
import HeroGalleryStrip from "./hero-modes/HeroGalleryStrip";
import HeroPhotoDrift from "./hero-modes/HeroPhotoDrift";
import HeroPhotoFloat from "./hero-modes/HeroPhotoFloat";
import HeroDiagonalSplit from "./hero-modes/HeroDiagonalSplit";
import HeroCircleSplit from "./hero-modes/HeroCircleSplit";

// Modes come in three flavours, all dispatched from this same map:
//
//   1. Static-only modes (``cinematic``, ``split``, ``profile``, …):
//      no animated sibling. Picking these never produces ambient
//      motion; ``hero_mode_animated`` only controls the entrance
//      animation.
//
//   2. Paired static + animated modes (``card`` / ``card_animated``,
//      ``centered`` / ``centered_animated``, ``gallery`` /
//      ``gallery_animated``): both share the same renderer because the
//      ambient motion lives in CSS classes driven by
//      ``modeDefinition.ambient_animation``. Picking the animated
//      variant is how a user opts into ambient motion for that family.
//
//   3. Animation-first modes (``mosaic``, ``spotlight_swap``,
//      ``photo_banner``, ``magazine_swap``, ``gallery_strip``):
//      motion is intrinsic to the layout — there's no static
//      counterpart and no on/off toggle. Each gets its own renderer
//      because the animation is structural (per-tile drift,
//      JS-driven cross-fade, per-frame pan, JS-driven vertical slide,
//      horizontal marquee), not a CSS overlay.
//
// Spatial *variations* (e.g. ``cinematic_right``) also share the
// renderer of their base mode — the renderer reads ``item.hero_mode``
// and flips the layout in place. This keeps the visual identity
// owned in one component while letting the catalog ship multiple
// related compositions.
const MODE_COMPONENTS = {
  cinematic: HeroCinematic,
  cinematic_right: HeroCinematic,
  split: HeroSplit,
  split_right: HeroSplit,
  // Soft-edge variants share the Split renderer — the fade is
  // applied client-side based on ``item.hero_mode``. See
  // ``isMediaFaded`` in HeroSplit.js for how the mode id drives the
  // mask gradient direction. Same dispatch pattern as
  // ``cinematic_right`` / ``split_right`` (one component handles
  // multiple compositional variations of the same family).
  split_fade: HeroSplit,
  split_fade_right: HeroSplit,
  // Visitor-driven sibling of ``split`` — same composition (media
  // on the left, text column on the right), but the media column
  // stretches on ``:hover`` / ``:focus-within`` while the text
  // column compresses to fill the remaining width. The renderer
  // reads ``item.hero_mode`` and applies the
  // ``hero-split-media--hover-expand`` class + a pair of CSS
  // variables (``--hero-split-media-rest-width`` /
  // ``--hero-split-media-hover-width``) that the CSS rule animates
  // between. Same dispatch pattern as the soft-edge fade variants
  // above (one component handles every Split family identity).
  split_hover_expand: HeroSplit,
  card: HeroCard,
  card_animated: HeroCard,
  centered: HeroCentered,
  centered_animated: HeroCentered,
  gallery: HeroGallery,
  gallery_animated: HeroGallery,
  // Flush (edge-to-edge) finishes share the same renderer as their
  // canonical sibling — ``HeroGallery`` reads ``item.hero_mode``
  // and flips the card chrome (corner radius / shadow / inter-card
  // gap) when the id ends in ``_flush``. The composition + motion
  // story are unchanged; only the per-card chrome differs. Same
  // dispatch pattern as the canonical Gallery entries above (one
  // component handles multiple finish variants of the same
  // family). The flush variant for ``gallery_strip`` lives below
  // next to the canonical strip entry for the same reason.
  gallery_flush: HeroGallery,
  gallery_animated_flush: HeroGallery,
  // ``gallery_proximity`` is the visitor-driven cadence sibling of
  // ``gallery_animated``: same vertical marquee composition, same
  // duplicated isometric track, same ``ambient_animation = "marquee-y"``
  // mounting the CSS keyframes. ``HeroGallery`` reads ``item.hero_mode``
  // and, when it's the proximity id, layers a ``mousemove`` listener on
  // top of the running CSS animation — the listener maps cursor
  // distance from the gallery's bounding rect into
  // ``Animation.playbackRate`` (Web Animations API) so the marquee
  // smoothly speeds up / slows down without resetting the loop. Same
  // dispatch pattern as the canonical / flush gallery entries above
  // (one component owns multiple visual / motion variants of the
  // same family).
  gallery_proximity: HeroGallery,
  // Profile frame-format siblings share one renderer. The mode id
  // selects circular avatar chrome vs. rounded-square headshot chrome
  // while the centered text/CTA composition stays identical.
  profile: HeroProfile,
  profile_rounded: HeroProfile,
  // The Magazine family ships four variants that all share two
  // renderers: ``HeroMagazine`` for the static pair (``magazine`` /
  // ``magazine_right``) and ``HeroMagazineSwap`` for the rotating
  // pair (``magazine_swap`` / ``magazine_swap_right``). Each
  // renderer reads ``item.hero_mode`` and flips the row direction
  // (and the title column's overlap margin) so the title can sit on
  // the left or the right while keeping the rest of the editorial
  // composition unchanged. Same dispatch pattern as the
  // ``cinematic`` / ``cinematic_right`` and ``split`` /
  // ``split_right`` pairs above (one component handles the
  // mirrored layout in place).
  magazine: HeroMagazine,
  magazine_right: HeroMagazine,
  stack: HeroStack,
  // The Mosaic family ships three motion variants that all share
  // one renderer — ``HeroMosaic`` reads ``item.hero_mode`` (and the
  // mode's ``ambient_animation`` id surfaced via
  // ``heroSlots.ambientAnimation``) to decide whether to mount the
  // per-tile drift class, sit perfectly still, or run the JS-driven
  // pair-swap rotation. Same dispatch pattern as the Photo Banner
  // family below (one component handles multiple variations of the
  // same family).
  mosaic: HeroMosaic,
  mosaic_static: HeroMosaic,
  mosaic_shuffle: HeroMosaic,
  // Flush (edge-to-edge) finishes share the same renderer as
  // their canonical sibling — ``HeroMosaic`` reads
  // ``item.hero_mode`` and flips the per-tile chrome (corner
  // radius / shadow / inter-tile gap) when the id ends in
  // ``_flush``. Composition + motion story are unchanged; only
  // the per-tile chrome differs. Same dispatch pattern as the
  // canonical Mosaic entries above (one component handles
  // multiple finish variants of the same family). Note: there is
  // intentionally no ``mosaic_flush`` entry here — the drift-
  // animated flush variant was retired because drift motion
  // visibly nudged adjacent cells away from each other, breaking
  // the "single continuous photographic surface" reading that the
  // flush finish exists to provide. Existing heroes are remapped
  // to ``mosaic_static_flush`` by backend migration 0039.
  mosaic_static_flush: HeroMosaic,
  mosaic_shuffle_flush: HeroMosaic,
  // Right-anchored spatial mirrors of every surviving Mosaic
  // member: collage on the RIGHT and text column on the LEFT.
  // ``HeroMosaic`` detects the trailing ``_right`` suffix and
  // reverses the row direction (``flex-row-reverse`` desktop /
  // ``flex-col-reverse`` mobile) — one renderer keeps the layout
  // flip in sync with the chrome flip via two orthogonal suffix
  // checks. The ``_flush`` and ``_right`` axes are independent
  // (the canonicaliser strips ``_right`` BEFORE checking for
  // ``_flush``) so combination ids like ``mosaic_static_flush_right``
  // pick up BOTH the square-corner / zero-gap chrome AND the row
  // reversal. See the §13b rationale block in
  // ``definitions.py`` for the design rationale.
  mosaic_right: HeroMosaic,
  mosaic_static_right: HeroMosaic,
  mosaic_static_flush_right: HeroMosaic,
  mosaic_shuffle_right: HeroMosaic,
  mosaic_shuffle_flush_right: HeroMosaic,
  spotlight_swap: HeroSpotlightSwap,
  // Layout sibling of ``spotlight_swap`` within the Spotlight
  // family — same JS-driven rotating-spotlight motion (one
  // featured photo at a time, slow rotation through the queue),
  // only the layout differs (photo on top full-width with text
  // stacked beneath, instead of the canonical horizontal split).
  // ``HeroSpotlightSwap`` reads ``item.hero_mode`` and switches
  // its layout in place — vertical stack for ``spotlight_swap_stack``,
  // horizontal split for the canonical ``spotlight_swap``. The
  // stack variant also enables a visitor-clickable dot indicator
  // for jumping to a specific photo (auto-rotation pauses
  // briefly after a click then resumes); the canonical
  // horizontal variant deliberately keeps the auto-only thumbnail
  // strip per the family's UX decision (see
  // ``isClickToNavigateVariant`` in ``HeroSpotlightSwap.js``).
  // Same dispatch pattern as the Magazine family: one component
  // handles multiple layout / motion variants of the same family
  // identity.
  spotlight_swap_stack: HeroSpotlightSwap,
  // Flush (edge-to-edge) finish for the stack layout — same
  // ``HeroSpotlightSwap`` renderer reads ``item.hero_mode`` and
  // flips the per-stage chrome (rounded corners / drop shadow /
  // gap padding) when the id matches ``spotlight_swap_stack_flush``,
  // and additionally absorbs the hero root's vertical padding
  // (via ``useHeroVerticalPadding``) so the photo butts straight
  // against the hero top edge. Composition + rotating-spotlight
  // motion + dot-overlay affordance are all unchanged; only the
  // chrome around the photo and its outer breathing room differ.
  // Same dispatch pattern as the canonical Photo Banner /
  // Mosaic flush entries above (one component handles multiple
  // finish variants of the same family). The horizontal sibling
  // ``spotlight_swap`` deliberately does NOT ship a flush
  // counterpart because its identity (photo beside text +
  // thumbnail strip beneath) doesn't read as "photo dominates
  // the canvas" — flush would just remove the rounded corners
  // off a relatively small photo column without earning its own
  // picker entry.
  spotlight_swap_stack_flush: HeroSpotlightSwap,
  // The Photo Banner family ships three motion variants that all
  // share one renderer — ``HeroPhotoBanner`` reads ``item.hero_mode``
  // (and the mode's ``ambient_animation`` id surfaced via
  // ``heroSlots.ambientAnimation``) to decide whether to mount the
  // per-frame pan, sit perfectly still, or animate frame widths.
  // Same dispatch pattern as ``cinematic_right`` / ``split_right``
  // (one component handles multiple variations of the same family).
  photo_banner: HeroPhotoBanner,
  photo_banner_static: HeroPhotoBanner,
  photo_banner_focus: HeroPhotoBanner,
  // Visitor-driven sibling of ``photo_banner_focus`` — same
  // width-breathing motion story (one frame stretches wide while
  // its siblings shrink) but the focus is triggered by the
  // visitor hovering / focusing a frame instead of a continuous
  // CSS keyframe rotating around the strip. ``HeroPhotoBanner``
  // reads ``item.hero_mode`` and mounts the hover-focus CSS
  // class on the strip / each frame instead of the ambient
  // motion class — see ``MODE_HOVER_FOCUS`` in HeroPhotoBanner.js
  // for the routing.
  photo_banner_hover_focus: HeroPhotoBanner,
  // Flush (edge-to-edge) finishes share the same renderer as
  // their canonical sibling — ``HeroPhotoBanner`` reads
  // ``item.hero_mode`` and flips the per-frame chrome (corner
  // radius / shadow / inter-frame gap / outer strip padding)
  // when the id ends in ``_flush``. Composition + motion story
  // are unchanged; only the per-frame chrome differs. Same
  // dispatch pattern as the canonical Photo Banner entries
  // above.
  photo_banner_flush: HeroPhotoBanner,
  photo_banner_static_flush: HeroPhotoBanner,
  photo_banner_focus_flush: HeroPhotoBanner,
  magazine_swap: HeroMagazineSwap,
  magazine_swap_right: HeroMagazineSwap,
  // Cross-fade siblings of the slide-up swap pair share the same
  // renderer — ``HeroMagazineSwap`` reads ``item.hero_mode`` and
  // switches the per-frame chrome from the slide-up translation to
  // an opacity-only cross-fade when the id starts with
  // ``magazine_fade``. Composition + rotating-photo cadence are
  // unchanged; only the transition style differs (calmer, no
  // vertical drift). Same dispatch pattern as the canonical /
  // _right pair above (one component handles multiple motion
  // variants of the same family). Right-anchored fade variant
  // gets the same row-direction flip as ``magazine_swap_right``,
  // owned by the renderer's ``isTitleRightAnchored`` helper.
  magazine_fade: HeroMagazineSwap,
  magazine_fade_right: HeroMagazineSwap,
  gallery_strip: HeroGalleryStrip,
  // Sole flush companion for the animation-first ``gallery_strip``
  // (the strip family ships exactly one flush member because there
  // is no static sibling). Renderer reads ``item.hero_mode`` to
  // flip card chrome the same way the other flush variants do.
  gallery_strip_flush: HeroGalleryStrip,
  // The Photo Drift family ships three direction siblings that all
  // share one renderer — ``HeroPhotoDrift`` reads ``item.hero_mode``
  // to decide whether to lay the scattered photo track out as a
  // vertical two-column column (drifting upward) or a horizontal
  // two-row band (drifting sideways). The ``ambient_animation`` id
  // surfaced via ``heroSlots.ambientAnimation`` (one of
  // ``photo-drift-up`` / ``photo-drift-left`` / ``photo-drift-right``)
  // selects the matching CSS keyframe so the track translates in
  // the right direction. Same dispatch pattern as the Photo Banner
  // / Mosaic families above (one component handles multiple
  // direction / motion variants of the same family).
  photo_drift_up: HeroPhotoDrift,
  photo_drift_left: HeroPhotoDrift,
  photo_drift_right: HeroPhotoDrift,
  // The Photo Float family ships four frame-format siblings that
  // all share one renderer — ``HeroPhotoFloat`` reads
  // ``item.hero_mode`` and flips the per-photo CSS class that owns
  // the chrome (rounded rectangle / circle mask / flush square /
  // mixed rounded+circle). The composition (text on the left,
  // floating cluster on the right) and the per-photo bob motion
  // (driven by the shared ``hero-ambient-photo-float`` keyframe
  // with per-photo ``--hero-float-index`` stagger) are unchanged
  // across siblings; only the chrome on each photo differs. The
  // mixed sibling is the only one whose chrome varies *per cell*
  // (the renderer alternates rounded ↔ circle along
  // ``CLUSTER_LAYOUT``); the other siblings paint one chrome story
  // across every cell. Same dispatch pattern as the Photo Banner
  // flush entries above (one component handles multiple finish
  // variants of the same family).
  photo_float_rounded: HeroPhotoFloat,
  photo_float_circle: HeroPhotoFloat,
  photo_float_flush: HeroPhotoFloat,
  photo_float_mixed: HeroPhotoFloat,
  // The Diagonal Split family ships two cut-angle siblings that all
  // share one renderer — ``HeroDiagonalSplit`` reads
  // ``item.hero_mode`` and swaps the clip-path polygon coordinates
  // between the canonical 25° subtle slash and the 45° corner-to-
  // corner X-pattern. The composition (half-image + half-text on
  // desktop with the seam tilted between them) and the slot
  // contract (gallery accepting up to 2 images, with the layout
  // adapting to 1 vs. 2 images) are shared across siblings; only
  // the polygon geometry differs. Same dispatch pattern as the
  // Photo Float flush entries above (one component handles multiple
  // identity-defining variants of the same family). Both siblings
  // are deliberately motionless — the geometric statement of the
  // diagonal cut IS the family's identity, so they ship without
  // ambient animation classes.
  diagonal_split: HeroDiagonalSplit,
  diagonal_split_left: HeroDiagonalSplit,
  diagonal_split_x: HeroDiagonalSplit,
  // The Circle Split family ships two arc-depth siblings that all
  // share one renderer — ``HeroCircleSplit`` reads
  // ``item.hero_mode`` and swaps the cubic Bezier control points
  // between the canonical gentle inward bulge and the deep near-
  // semicircular bulge that makes the photo read as a literal
  // disc segment. Composition (half-image + half-text on desktop
  // with a curved seam between them) and slot contract (gallery
  // accepting up to 2 images, with the layout adapting to 1 vs.
  // 2 images) are shared across siblings; only the curve depth
  // differs, owned entirely by the renderer keying off the id.
  // Same dispatch pattern as the Diagonal Split entries above
  // (one component handles multiple identity-defining variants of
  // the same family). Both siblings are deliberately motionless
  // — the geometric statement of the curved seam IS the family's
  // identity, so they ship without ambient animation classes
  // (mirrors Diagonal Split / Split Fade in the half-image
  // neighbourhood).
  circle_split: HeroCircleSplit,
  circle_split_deep: HeroCircleSplit,
  circle_split_organic: HeroCircleSplit,
};

const HeroAutomatic = ({ item, containerWidth }) => {
  const heroBase = useHeroBase(item, containerWidth);
  const heroSlots = useHeroSlots(item);
  const ModeComponent = MODE_COMPONENTS[item?.hero_mode];

  if (!ModeComponent) {
    return <HeroManual item={item} containerWidth={containerWidth} />;
  }

  // In an automatic hero layout the MODE owns the spacing around its photo
  // slots, so a newly-created image's own per-side margin (default 1) must not
  // apply to the empty "No Image Selected" placeholder — otherwise it gets
  // pushed into the slot's `overflow-hidden` clip and renders cut. The
  // placeholder is a `<div>` (`.image-empty-placeholder` in `Image.js`), so we
  // zero its margin via a cascade override. `display: contents` keeps this
  // wrapper out of layout (no box) so it never affects the hero's sizing /
  // full-bleed breakout, while the descendant selector still reaches the
  // placeholder. PADDING is left untouched.
  return (
    <div className="contents [&_.image-empty-placeholder]:!m-0">
      <ModeComponent
        item={item}
        heroBase={heroBase}
        heroSlots={heroSlots}
        containerWidth={containerWidth}
      />
    </div>
  );
};

export default HeroAutomatic;
